/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import {
  SharedPicker,
  SharedPickerOption,
  SharedPickerOptionValue,
} from "components/shared/SharedPicker";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export const ModelVersionPicker: React.FC<{
  modelVersion: ModelVersion;
  onSelected: (modelVersion: ModelVersion) => void;
  isQuiet?: boolean;
  pickerSize?: "s" | "m" | "l" | "xl";
  pickerStyles?: React.CSSProperties;
}> = ({
  modelVersion,
  onSelected,
  isQuiet = false,
  pickerSize = "m",
  pickerStyles = { width: "40px" },
}) => {
  const modelVersionOptions: SharedPickerOption[] = [
    {
      value: ModelVersion.v2,
      name: I18n.t("enhance_speech.model_version_picker.v2_label"),
    },
    {
      value: ModelVersion.v1,
      name: I18n.t("enhance_speech.model_version_picker.v1_label"),
    },
  ];
  const onOptionSelected = (optionValue: SharedPickerOptionValue): void => {
    onSelected(optionValue as ModelVersion);
  };
  return (
    <SharedPicker
      options={modelVersionOptions}
      selectedValue={modelVersion}
      onOptionSelected={onOptionSelected}
      isQuiet={isQuiet}
      pickerSize={pickerSize}
      pickerStyles={pickerStyles}
      ariaLabel={"Enhance speech model version for this project"}
    />
  );
};
